/** @jsxImportSource @emotion/react */
import * as React from "react";
import { jsx, Theme } from "@emotion/react";
import { IconArrow, IconArrowProps } from "atoms/icons";

const borderSize = "2px";
const transformTimeFn = "ease 0.15s";
const translateDist = "4px";

const buttonCSS = (theme: Theme) => ({
  ...theme.fonts.display,
  cursor: "pointer",
  border: `${borderSize} solid`,
  borderRadius: theme.borderRadius.md,
  display: "inline-flex",
  alignItems: "center",
  fontSize: theme.fontSizes.md,
  lineHeight: 1.3,
  minHeight: "2.5rem",
  padding: "0.5em 1.2em",
  position: "relative" as const,
  textAlign: "center" as const,
  textDecoration: "none",
  textTransform: "uppercase" as const,
  transition: `transform ${transformTimeFn}, border-color ${transformTimeFn}, color ${transformTimeFn}`,
  "&:hover, &:focus-visible": {
    textDecoration: "none",
  },
  "&:hover": {
    transform: `translate(${translateDist}, ${translateDist})`,
  },

  "&:active": {
    transform: "scale(0.9, 1.1)",
  },
  // Fix for transform on hover, see https://codepen.io/zachfedor/pen/MWrrgyq?editors=1100
  "&::before": {
    borderRadius: theme.borderRadius.md,
    bottom: `-${borderSize}`,
    content: "''",
    display: "block",
    left: `-${borderSize}`,
    position: "absolute" as const,
    right: `-${borderSize}`,
    top: `-${borderSize}`,
    transition: `transform ${transformTimeFn}, right ${transformTimeFn}, bottom ${transformTimeFn}`,
  },
  "&:hover::before": {
    transform: `translate(-${translateDist}, -${translateDist})`,
    right: `calc(-${translateDist} - ${borderSize})`,
    bottom: `calc(-${translateDist} - ${borderSize})`,
  },
  "&[disabled]": {
    background: theme.colors.bgLight,
    color: "#c4c4c4",
    borderColor: "#c4c4c4",
    "&:hover": {
      transform: "unset",
      borderColor: "#c4c4c4",
    },
  },
});

const buttonVariants = {
  primary: (theme: Theme) => ({
    background: theme.colors.bgLight,
    borderColor: theme.colors.fg,
    color: theme.colors.fg,
    "&:hover, &:focus-visible": {
      borderColor: theme.colors.fg,
    },
    "&:focus-visible": {
      outlineColor: theme.colors.fg,
    },
  }),
  gradient: (theme: Theme) => ({
    color: theme.colors.fg,
    background: [theme.colors.bg, theme.gradients.linear3],
    borderColor: theme.colors.fg,
    "&:hover, &:focus-visible": {
      borderColor: theme.colors.fg,
    },
    "&:focus-visible": {
      outlineColor: theme.colors.fg,
    },
  }),
  default: (theme: Theme) =>
    ({
      ...theme.fonts.body,
      background: theme.colors.gray10,
      border: 0,
      color: theme.colors.fg,
      fontSize: theme.fontSizes.sm,
      fontWeight: "bold",
      paddingLeft: ".9em",
      paddingRight: ".9em",
      textTransform: "unset",
      "&:hover, &:focus-visible": {
        transform: "unset",
        backgroundColor: theme.colors.gray20,
        color: theme.colors.fg,
      },
      "&:focus-visible": {
        outlineColor: theme.colors.fg,
      },
      "&:active": {
        transform: "unset",
      },
      "&::before": {
        content: "unset",
      },
      ".icon-chevron": {
        marginRight: "-2px",
        width: ".6em",
      },
    } as const),
  blank: (theme: Theme) =>
    ({
      ...theme.fonts.body,
      color: theme.colors.fg,
      background: "none",
      border: 0,
      lineHeight: 1.3,
      padding: 0,
      textDecoration: "underline",
      textTransform: "none",
      minHeight: 0,
      "&:hover, &:focus-visible": {
        transform: "unset",
        color: theme.colors.primary,
      },
      "&:focus-visible": {
        outlineColor: theme.colors.fg,
      },
      "&:active": {
        transform: "unset",
      },
      "&::before": {
        content: "unset",
      },
    } as const),
};

const buttonSizes = {
  xxs: (theme: Theme) => ({
    borderRadius: theme.borderRadius.xs,
    fontSize: theme.fontSizes.xxxs,
    minHeight: "unset",
  }),
  xs: (theme: Theme) => ({
    borderRadius: theme.borderRadius.xs,
    fontSize: theme.fontSizes.xxs,
    minHeight: "unset",
  }),
  sm: (theme: Theme) => ({
    borderRadius: theme.borderRadius.sm,
    fontSize: theme.fontSizes.xs,
    minHeight: "unset",
    "form &": {
      minHeight: "2.5rem",
    },
  }),
  md: (theme: Theme) => ({
    fontSize: theme.fontSizes.sm,
    minHeight: "unset",
    "form &": {
      minHeight: "2.5rem",
    },
  }),
  lg: (theme: Theme) => ({
    // default
    // fontSize: theme.fontSizes.md,
  }),
};

const arrowCSS = {
  marginLeft: "0.8rem",
};

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  type?: "submit" | "button";
  variant?: keyof typeof buttonVariants;
  as?: React.ElementType;
  size?: keyof typeof buttonSizes;
  arrow?: null | IconArrowProps["dir"];
  arrowProps?: Omit<IconArrowProps, "dir">;
}

const Button = ({
  children,
  variant = "default",
  type = "button",
  size = "lg",
  as = "button",
  arrow = null,
  arrowProps,
  ...props
}: ButtonProps) =>
  jsx(
    as,
    {
      css: [buttonCSS, buttonVariants[variant], buttonSizes[size]],
      type,
      ...props,
    },
    arrow ? (
      <>
        {children}
        {variant !== "blank" && (
          <IconArrow dir={arrow} css={arrowCSS} {...arrowProps} />
        )}
      </>
    ) : (
      children
    )
  );

export { Button, buttonCSS, buttonVariants, buttonSizes };
